import { CarvanaCertifiedSpot, HundredDay, SevenDayGuarantee } from '@carvana/ds-icons';
import React from 'react';
import MakeLogo, { hasMakeLogo } from '|/components/make-logo';
import { isNewVehicle } from '|/helpers';
import { deriveManufacturerWarranty } from '|/helpers/manufacturer-warranty';
import { ReviewSummaryResponse } from '|/services/purchase-api';
import styles from '../price-details.module.css';

interface IncludedWithPurchaseSectionProps {
  summary: ReviewSummaryResponse;
}

const IncludedWithPurchaseSection = ({ summary }: IncludedWithPurchaseSectionProps) => {
  const warrantyRows = deriveManufacturerWarranty(summary.vehicle);
  const make = summary.vehicle?.make;
  // The 100-day warranty and Carvana Certified inspection are reconditioning promises on
  // used inventory; neither applies to a new car.
  const isNewCar = isNewVehicle(summary.vehicle);

  return (
    <section className={styles.majorSection} data-testid="pp_price-details_included-with-purchase">
      <div className={styles.majorHeading}>
        <h3 className={styles.majorTitle}>Included with your purchase</h3>
      </div>

      <div className={styles.inclusionRows}>
        {warrantyRows.length > 0 && (
          <div className={styles.inclusionRow} data-testid="pp_price-details_factory-warranty">
            {hasMakeLogo(make) && (
              <span className={styles.makeCircle}>
                <MakeLogo make={make} size={18} />
              </span>
            )}
            <h4 className={styles.inclusionTitle}>Factory Warranty</h4>
            <div className={styles.warrantyTermsList}>
              {warrantyRows.map(row => (
                <p className={styles.warrantyTerms} key={row.key}>
                  {row.text}
                </p>
              ))}
            </div>
            <p className={styles.warrantyNote}>
              See manufacturer&apos;s website for warranty details.
            </p>
          </div>
        )}

        <div className={styles.inclusionRow} data-testid="pp_price-details_seven-day-guarantee">
          <SevenDayGuarantee size={29} aria-hidden />
          <h4 className={styles.inclusionTitle}>7-Day Money Back Guarantee</h4>
          <p className={styles.inclusionCopy}>
            Don&apos;t love it? Return it or swap it for a new ride.
          </p>
        </div>

        {!isNewCar && (
          <div className={styles.inclusionRow} data-testid="pp_price-details_limited-warranty">
            <HundredDay size={29} aria-hidden />
            <h4 className={styles.inclusionTitle}>100-Day Limited Warranty</h4>
            <p className={styles.inclusionCopy}>
              Peace of mind for your first 100 days or 4,189 miles.
            </p>
          </div>
        )}

        {!isNewCar && (
          <div className={styles.inclusionRow} data-testid="pp_price-details_carvana-certified">
            <CarvanaCertifiedSpot size={29} aria-hidden />
            <h4 className={styles.inclusionTitle}>Carvana Certified</h4>
            <p className={styles.inclusionCopy}>
              All Carvana Certified vehicles go through a 150-point inspection so that you can feel
              good about the quality of your car.
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default IncludedWithPurchaseSection;
